import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { productService, newsletterService, siteConfigService, blogService } from '../services/api';
import { updateMetaTags } from '../utils/seo';
import {
  Box, BarChart, Globe, ChevronRight, Tag, Calendar, User, ArrowRight,
  TrendingUp, Sparkles, Zap, Award, Users2, Package, Truck, Target,
  ChevronDown, Mail, Phone, MapPin, Star, ShoppingBag
} from 'lucide-react';
import AdSlot from './AdSlot';
import LocalSpotlight from './LocalSpotlight';

function Home() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [sections, setSections] = useState([]);
  const [settings, setSettings] = useState({});
  const [activeSlider, setActiveSlider] = useState(0);
  const [activePortfolioTab, setActivePortfolioTab] = useState('food');
  const [latestBlogs, setLatestBlogs] = useState([]);
  const [showDelightsModal, setShowDelightsModal] = useState(false);

  useEffect(() => {
    loadHomeContent();
  }, []);

  useEffect(() => {
    if (sliders.length > 1) {
      const timer = setInterval(() => {
        setActiveSlider((prev) => (prev + 1) % sliders.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [sliders]);

  useEffect(() => {
    const s = settings || {};
    updateMetaTags({
      title: s.meta_title || s.site_name || 'Ortus',
      description: s.meta_description || s.site_tagline || '',
      keywords: s.meta_keywords || '',
      favicon: s.site_favicon,
      siteName: s.site_name || 'Ortus'
    });
  }, [settings]);

  const loadHomeContent = async () => {
    try {
      const [products, sliderData, sectionData, settingsData, blogData] = await Promise.all([
        productService.index({ featured: 1, active_only: 1 }),
        siteConfigService.sliders('home'),
        siteConfigService.sections('home'),
        siteConfigService.settings(),
        blogService.index({ status: 'published', per_page: 3 }).catch(() => []),
      ]);
      setFeaturedProducts(Array.isArray(products) ? products : products?.data || []);
      setSliders(Array.isArray(sliderData) ? sliderData : sliderData?.data || []);
      setSections(Array.isArray(sectionData) ? sectionData : sectionData?.data || []);
      setSettings(settingsData?.data || settingsData || {});
      setLatestBlogs(Array.isArray(blogData) ? blogData.slice(0, 3) : (blogData?.data || []).slice(0, 3));
    } catch (error) {
      console.error('Error loading home content:', error);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await newsletterService.subscribe({ email });
      setSubscribed(true);
      setEmail('');
    } catch (error) {
      console.error('Subscription failed:', error);
    }
  };

  const pillars = [
    {
      title: 'Diverse Portfolio',
      desc: 'A food-led FMCG company with considerable presence in home, personal care, and beauty categories.',
      icon: 'box',
      gradient: 'from-orange-500 via-red-500 to-pink-500',
      bg: 'bg-orange-50',
      text: 'text-orange-600',
      glow: 'shadow-orange-500/20',
    },
    {
      title: 'Data Science',
      desc: 'We work with real-time customer data to deliver frequency, understand repeats, and create promotions.',
      icon: 'chart',
      gradient: 'from-blue-500 via-indigo-500 to-purple-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      glow: 'shadow-blue-500/20',
    },
    {
      title: 'Digital Distribution',
      desc: 'A digital-commerce first and modern retail-led distribution model for all our brands.',
      icon: 'globe',
      gradient: 'from-teal-500 via-cyan-500 to-blue-500',
      bg: 'bg-teal-50',
      text: 'text-teal-600',
      glow: 'shadow-teal-500/20',
    },
  ];

  const stats = [
    { icon: 'package', value: '50+', label: 'Products', gradient: 'from-orange-500 to-red-500' },
    { icon: 'users', value: '10M+', label: 'Consumers', gradient: 'from-blue-500 to-indigo-600' },
    { icon: 'truck', value: '500+', label: 'Distributors', gradient: 'from-teal-500 to-cyan-500' },
    { icon: 'award', value: '15+', label: 'Awards', gradient: 'from-purple-500 to-pink-500' },
  ];

  const iconMap = {
    box: Box, chart: BarChart, globe: Globe, package: Package,
    users: Users2, truck: Truck, award: Award, target: Target, zap: Zap, star: Star
  };

  const renderIcon = (name, size = 48) => {
    const Icon = iconMap[name] || Box;
    return <Icon size={size} />;
  };

  const brands = [
    { name: 'Foods & Beverages', category: '', slug: 'food', tab: 'food', hideCategory: true },
    { name: 'Golden Harvest', category: '', slug: 'food', tab: 'food', hideCategory: true },
    { name: 'Fresh & Pure', category: '', slug: 'food', tab: 'food', hideCategory: true },
    { name: 'Local Spotlight', category: '', slug: 'food', tab: 'food', hideCategory: true, isLocalSpotlight: true },
    { name: 'Ortus Delights', category: '', slug: 'food', tab: 'food', hideCategory: true, isDelights: true },
    { name: 'Ortus Home', category: 'Home Care', slug: 'home-care', tab: 'home-care' },
    { name: 'CleanMate', category: 'Home Care', slug: 'home-care', tab: 'home-care' },
    { name: 'CareMate', category: 'Home Care', slug: 'home-care', tab: 'home-care' },
    { name: 'Voom', category: 'Home Care', slug: 'home-care', tab: 'home-care' },
    { name: 'Ortus Care', category: 'Body Care', slug: 'personal-care', tab: 'body-care' },
    { name: 'Think Skin', category: 'Body Care', slug: 'personal-care', tab: 'body-care' },
    { name: 'Prim', category: 'Body Care', slug: 'personal-care', tab: 'body-care' },
    { name: 'Pratha', category: 'Body Care', slug: 'personal-care', tab: 'body-care' },
    { name: 'Ortus Beauty', category: 'Opulent Edge', slug: 'beauty', tab: 'opulent-edge' },
    { name: 'Kara', category: 'Opulent Edge', slug: 'beauty', tab: 'opulent-edge' },
    { name: 'TS', category: 'Opulent Edge', slug: 'beauty', tab: 'opulent-edge' },
    { name: 'Mysst', category: 'Opulent Edge', slug: 'beauty', tab: 'opulent-edge' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Enhanced */}
      <section className="relative bg-slate-900 text-white overflow-hidden min-h-[640px] flex items-center">
        {sliders.length > 0 ? (
          sliders.map((slider, idx) => (
            <div
              key={slider.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${idx === activeSlider ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/70 to-slate-900/30 z-10" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-slate-900/40 z-10" />
              {slider.video_url ? (
                <video
                  src={slider.video_url}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : slider.image ? (
                <img
                  src={slider.image}
                  alt={slider.title || 'Hero'}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-red-900 via-slate-900 to-indigo-900" />
              )}

              {/* Floating decorative orbs */}
              <div className="absolute top-20 right-20 w-72 h-72 bg-red-500/10 rounded-full blur-3xl animate-pulse z-5" />
              <div className="absolute bottom-20 left-20 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl animate-pulse z-5" style={{ animationDelay: '1s' }} />

              <div className="relative z-20 max-w-7xl mx-auto px-4 h-full flex items-center">
                <div className="max-w-3xl py-24 md:py-32">
                  <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-1.5 mb-6 animate-fade-in-up">
                    <Sparkles size={14} className="text-red-400" />
                    <p className="text-red-400 font-semibold tracking-wide uppercase text-xs">
                      {slider.subtitle || 'FMCG 2.0'}
                    </p>
                  </div>
                  <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.1] animate-fade-in-up delay-100 text-white">
                    {slider.title || settings.site_tagline || 'For the Fast Moving Consumer Generation'}
                  </h1>
                  <div
                    className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl animate-fade-in-up delay-200 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: slider.description || "Building brands for India's next generation of consumers. We understand evolving aspirations and deliver innovative products across food, home care, and personal care." }}
                  />
                  <div className="flex flex-wrap gap-4 animate-fade-in-up delay-300">
                    <Link
                      to={slider.link || '/about'}
                      className="group bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-4 rounded-xl hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300 font-bold flex items-center gap-2 hover:scale-105"
                    >
                      {slider.link_text || 'About Us'}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/shop"
                      className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl hover:bg-white hover:text-slate-900 transition-all duration-300 font-bold flex items-center gap-2"
                    >
                      <ShoppingBag size={18} />
                      Explore Products
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="relative z-20 max-w-7xl mx-auto px-4 py-24 md:py-32">
             <div className="max-w-3xl">
                <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-1.5 mb-6">
                  <Sparkles size={14} className="text-red-400" />
                  <p className="text-red-400 font-semibold tracking-wide uppercase text-xs">FMCG 2.0</p>
                </div>
                <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.1] text-white">
                  For the Fast Moving Consumer Generation
                </h1>
                <p className="text-lg md:text-xl text-slate-300 mb-10 max-w-2xl leading-relaxed">
                  Building brands for India's next generation of consumers.
                </p>
             </div>
          </div>
        )}

        {/* Carousel Controls */}
        {sliders.length > 1 && (
          <div className="absolute bottom-8 left-0 right-0 z-30 flex justify-center gap-2">
            {sliders.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setActiveSlider(idx)}
                className={`h-1.5 transition-all duration-300 rounded-full ${idx === activeSlider ? 'w-10 bg-gradient-to-r from-red-500 to-orange-500' : 'w-2 bg-white/30 hover:bg-white/50'}`}
              />
            ))}
          </div>
        )}

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-8 z-30 hidden md:flex flex-col items-center gap-2 text-slate-400">
          <span className="text-[10px] uppercase tracking-widest font-semibold">Scroll</span>
          <ChevronDown size={20} className="animate-bounce" />
        </div>
      </section>

      {/* Stats Bar - New */}
      <section className="relative -mt-1 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-12 border-y border-slate-700/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => {
              const Icon = iconMap[stat.icon] || Package;
              return (
                <div key={i} className="flex items-center gap-4 group">
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-black text-white">{stat.value}</div>
                    <div className="text-xs text-slate-400 uppercase tracking-wider font-semibold">{stat.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hero Ad Slot */}
      <div className="max-w-7xl mx-auto px-4 mt-8">
        <AdSlot position="homepage_main" className="h-[200px]" />
      </div>

      {sections.filter(s => s.is_active).sort((a, b) => {
        const order = { portfolio: 0, pillars: 1 };
        const aOrder = order[a.section_type] ?? 99;
        const bOrder = order[b.section_type] ?? 99;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (a.sort_order || 0) - (b.sort_order || 0);
      }).map((s) => {
        const dark = s.bg_color === 'slate-900';
        const bgClass = s.bg_color === 'slate-50' ? 'bg-slate-50' : s.bg_color === 'red-50' ? 'bg-red-50' : s.bg_color === 'slate-900' ? 'bg-slate-900 text-white' : 'bg-white';
        
        return (
          <section key={s.id} id={s.section_type} className={`py-20 ${bgClass}`}>
            <div className="max-w-7xl mx-auto px-4">
              {s.section_type === 'image_text' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                  {s.image && <img src={s.image} alt={s.title} className="rounded-3xl shadow-2xl w-full h-[450px] object-cover" />}
                  <div className={s.image ? '' : 'md:col-span-2 max-w-3xl mx-auto text-center'}>
                    {s.title && <h2 className={`text-3xl md:text-5xl font-black mb-6 ${dark ? 'text-white' : 'text-slate-900'}`}>{s.title}</h2>}
                    {s.content && <div className={`text-lg leading-relaxed ${dark ? 'text-slate-300' : 'text-slate-600'}`} dangerouslySetInnerHTML={{ __html: s.content }} />}
                    {s.button_link && (
                      <Link to={s.button_link} className="inline-flex items-center gap-2 mt-8 bg-gradient-to-r from-red-600 to-orange-500 text-white px-8 py-3.5 rounded-xl font-bold hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300 hover:scale-105">
                        {s.button_text || 'Learn More'} <ArrowRight size={16} />
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {s.section_type === 'pillars' && (
                <div>
                  <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-4 py-1.5 mb-4">
                      <Zap size={14} className="text-red-500" />
                      <span className="text-red-600 font-bold text-xs uppercase tracking-wider">Why Choose Us</span>
                    </div>
                    <h2 className={`text-3xl md:text-5xl font-black mb-4 ${dark ? 'text-white' : 'text-slate-900'}`}>{s.title || 'Key Pillars'}</h2>
                    {s.content && <p className={`max-w-2xl mx-auto text-lg ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{s.content}</p>}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {(s.meta?.items || pillars).map((pillar, i) => {
                      const colors = [
                        { gradient: 'from-orange-500 via-red-500 to-pink-500', bg: 'bg-orange-50', text: 'text-orange-600', glow: 'shadow-orange-500/20' },
                        { gradient: 'from-blue-500 via-indigo-500 to-purple-600', bg: 'bg-blue-50', text: 'text-blue-600', glow: 'shadow-blue-500/20' },
                        { gradient: 'from-teal-500 via-cyan-500 to-blue-500', bg: 'bg-teal-50', text: 'text-teal-600', glow: 'shadow-teal-500/20' },
                      ];
                      const c = pillar.gradient ? pillar : colors[i % colors.length];
                      return (
                        <div
                          key={i}
                          className={`${dark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-100'} p-8 rounded-3xl shadow-sm border text-center hover:shadow-2xl ${c.glow} transition-all duration-500 group relative overflow-hidden hover:-translate-y-2`}
                        >
                          <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${c.gradient}`} />
                          <div className={`absolute -top-20 -right-20 w-40 h-40 bg-gradient-to-br ${c.gradient} opacity-5 rounded-full blur-2xl group-hover:opacity-10 transition-opacity duration-500`} />
                          <div className={`mb-6 w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br ${c.gradient} flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg ${c.glow}`}>
                            <div className="text-white">
                              {pillar.icon && typeof pillar.icon === 'string' ? (
                                renderIcon(pillar.icon, 36)
                              ) : pillar.icon || (
                                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                              )}
                            </div>
                          </div>
                          <h3 className={`text-xl font-black mb-3 ${dark ? 'text-white' : 'text-slate-900'}`}>{pillar.title}</h3>
                          <p className={`text-sm leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{pillar.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {s.section_type === 'portfolio' && (
                <div id="portfolio-section">
                  <div className="text-center mb-12">
                    <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-100 rounded-full px-4 py-1.5 mb-4">
                      <Target size={14} className="text-indigo-500" />
                      <span className="text-indigo-600 font-bold text-xs uppercase tracking-wider">Our Brands</span>
                    </div>
                    <h2 className={`text-3xl md:text-5xl font-black mb-4 ${dark ? 'text-white' : 'text-slate-900'}`}>{s.title || 'Our Portfolio'}</h2>
                    {s.content && <p className={`max-w-3xl mx-auto text-lg ${dark ? 'text-slate-400' : 'text-slate-600'}`}>{s.content.replace(/FMCG/gi, '')}</p>}
                  </div>

                  {/* Portfolio Tabs - Enhanced */}
                  <div className="flex flex-wrap justify-center gap-3 mb-12">
                    {[
                    ].map((tab) => (
                      <button
                        key={tab.key}
                        onClick={() => setActivePortfolioTab(tab.key)}
                        className={`px-6 py-3.5 rounded-2xl font-bold text-sm uppercase tracking-wider transition-all duration-300 flex items-center gap-2 ${
                          activePortfolioTab === tab.key
                            ? 'bg-gradient-to-r ' + tab.color + ' text-white shadow-lg scale-105 border-0'
                            : dark
                              ? 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white border border-slate-700'
                              : tab.lightColor + ' border hover:scale-105'
                        }`}
                      >
                        <span className="text-base">{tab.icon}</span>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Tab Content */}
                  {activePortfolioTab === 'local-spotlight' ? (
                    <LocalSpotlight />
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                      {(s.meta?.brands || brands)
                        .filter((brand) => brand.tab === activePortfolioTab)
                        .map((brand, i) => {
                          if (brand.isDelights) {
                            return (
                              <button
                                key={i}
                                onClick={() => setShowDelightsModal(true)}
                                className={`${dark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-200 hover:border-red-300 hover:shadow-xl'} group rounded-2xl p-8 text-center border transition-all duration-300 cursor-pointer`}
                              >
                                <div className="h-20 w-20 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-red-500 to-red-700 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-red-600/20">
                                  <Sparkles size={36} className="text-white" />
                                </div>
                                <h3 className={`font-bold uppercase ${dark ? 'text-white' : 'text-slate-900'}`}>{brand.name}</h3>
                                <p className="text-xs text-red-500 mt-1 uppercase tracking-widest font-semibold">Click to explore</p>
                              </button>
                            );
                          }
                          if (brand.isLocalSpotlight) {
                            return (
                              <button
                                key={i}
                                onClick={() => setActivePortfolioTab('local-spotlight')}
                                className={`${dark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-200 hover:border-red-300 hover:shadow-xl'} group rounded-2xl p-8 text-center border transition-all duration-300 cursor-pointer`}
                              >
                                <div className="h-20 w-20 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-500/20">
                                  <span className="text-3xl">📍</span>
                                </div>
                                <h3 className={`font-bold uppercase ${dark ? 'text-white' : 'text-slate-900'}`}>{brand.name}</h3>
                                <p className="text-xs text-amber-500 mt-1 uppercase tracking-widest font-semibold">Click to explore</p>
                              </button>
                            );
                          }
                          return (
                            <Link
                              key={i}
                              to={brand.url || `/brands/${brand.slug}`}
                              className={`${dark ? 'bg-slate-800 border-slate-700 hover:bg-slate-700' : 'bg-white border-slate-200 hover:border-red-300 hover:shadow-xl'} group rounded-2xl p-8 text-center border transition-all duration-300`}
                            >
                              <div className="h-20 w-20 mx-auto mb-4 flex items-center justify-center bg-slate-50 rounded-2xl overflow-hidden border border-slate-100 group-hover:bg-red-50 transition">
                                {settings.site_logo ? (
                                  <img src={settings.site_logo} alt={brand.name} className="h-16 w-16 object-contain group-hover:scale-110 transition-transform duration-300" />
                                ) : (
                                  <div className="h-20 w-20 flex items-center justify-center text-red-600 font-black text-xl">
                                    {brand.name.charAt(0)}
                                  </div>
                                )}
                              </div>
                              <h3 className={`font-bold uppercase ${dark ? 'text-white' : 'text-slate-900'}`}>{brand.name}</h3>
                              {!brand.hideCategory && brand.category && (
                                <p className="text-xs text-slate-500 mt-1 uppercase tracking-widest font-semibold">{brand.category}</p>
                              )}
                            </Link>
                          );
                        })}
                    </div>
                  )}

                  {/* Ortus Delights Modal */}
                  {showDelightsModal && (
                    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowDelightsModal(false)}>
                      <div className="bg-white rounded-3xl p-10 max-w-2xl w-full shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-8">
                          <div>
                            <h3 className="text-2xl font-black text-slate-900">Ortus Delights</h3>
                            <p className="text-slate-500 text-sm mt-1">Explore our premium sub-categories</p>
                          </div>
                          <button onClick={() => setShowDelightsModal(false)} className="p-2 rounded-xl hover:bg-slate-100 transition">
                            <svg className="w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          {[
                            { key: 'home-care', label: 'Home Care', icon: '🏠', color: 'from-blue-500 to-cyan-500', bgColor: 'bg-blue-50', textColor: 'text-blue-600', desc: 'Cleaning & home essentials' },
                            { key: 'body-care', label: 'Body Care', icon: '🧴', color: 'from-teal-500 to-red-500', bgColor: 'bg-teal-50', textColor: 'text-teal-600', desc: 'Personal hygiene & wellness' },
                            { key: 'opulent-edge', label: 'Opulent Edge', icon: '✨', color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-50', textColor: 'text-purple-600', desc: 'Premium beauty & cosmetics' },
                          ].map((sub) => (
                            <button
                              key={sub.key}
                              onClick={() => {
                                setShowDelightsModal(false);
                                setActivePortfolioTab(sub.key);
                                document.getElementById('portfolio-section')?.scrollIntoView({ behavior: 'smooth' });
                              }}
                              className="group rounded-2xl p-6 text-center border border-slate-200 hover:border-transparent hover:shadow-xl transition-all duration-300 hover:scale-105"
                            >
                              <div className={`h-16 w-16 mx-auto mb-4 flex items-center justify-center bg-gradient-to-br ${sub.color} rounded-2xl group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                <span className="text-3xl">{sub.icon}</span>
                              </div>
                              <h4 className="font-bold text-slate-900 uppercase text-sm tracking-wider">{sub.label}</h4>
                              <p className="text-xs text-slate-500 mt-1">{sub.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {s.section_type === 'local_spotlight' && (
                <LocalSpotlight isStandalone={false} />
              )}

              {s.section_type === 'cta' && (
                <div className="bg-gradient-to-r from-red-600 via-red-600 to-orange-500 rounded-3xl p-10 md:p-16 text-center text-white relative overflow-hidden">
                  <div className="relative z-10 max-w-3xl mx-auto">
                    <h2 className="text-3xl md:text-5xl font-black mb-6">{s.title || 'Ready to Partner With Us?'}</h2>
                    <p className="text-lg text-red-50 mb-10 opacity-90">{s.content || 'Join our distribution network and be part of India\'s fastest growing FMCG platform.'}</p>
                    <Link 
                      to={s.button_link || '/contact'} 
                      className="inline-flex items-center gap-2 bg-white text-red-600 px-10 py-4 rounded-xl font-black hover:bg-red-50 transition shadow-xl hover:scale-105"
                    >
                      {s.button_text || 'Get Started'} <ArrowRight size={18} />
                    </Link>
                  </div>
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-orange-300/20 rounded-full blur-3xl"></div>
                </div>
              )}

              {s.section_type === 'content' && (
                <div className="max-w-4xl mx-auto">
                  {s.title && <h2 className={`text-3xl font-bold mb-8 text-center ${dark ? 'text-white' : 'text-slate-900'}`}>{s.title}</h2>}
                  <div className={`prose prose-lg max-w-none ${dark ? 'text-slate-300 prose-invert' : 'text-slate-600'}`} dangerouslySetInnerHTML={{ __html: s.content }} />
                </div>
              )}

              {s.section_type === 'feature_grid' && (
                <div>
                  {s.title && <h2 className={`text-3xl md:text-5xl font-black text-center mb-12 ${dark ? 'text-white' : 'text-slate-900'}`}>{s.title}</h2>}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {(s.meta?.items || []).map((item, idx) => {
                      const gradients = [
                        'from-orange-500 to-red-500',
                        'from-blue-500 to-indigo-600',
                        'from-teal-500 to-cyan-500',
                        'from-purple-500 to-pink-500',
                        'from-amber-500 to-orange-500',
                        'from-green-500 to-emerald-500',
                      ];
                      const gradient = gradients[idx % gradients.length];
                      return (
                        <div key={idx} className={`${dark ? 'bg-slate-800/50' : 'bg-white'} border border-slate-200 rounded-3xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 text-center hover:-translate-y-1 group`}>
                          <div className={`w-16 h-16 bg-gradient-to-br ${gradient} text-white rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl shadow-lg group-hover:scale-110 transition-transform duration-300`}>{item.icon || '✨'}</div>
                          <h3 className={`font-black text-xl mb-3 ${dark ? 'text-white' : 'text-slate-900'}`}>{item.title}</h3>
                          <p className={`text-sm leading-relaxed ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{item.desc}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {s.section_type === 'dynamic_showcase' && (
                <div className="relative">
                  {/* Header */}
                  <div className="text-center mb-14">
                    {s.subtitle && (
                      <p className="text-sm font-bold uppercase tracking-widest mb-3 bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent">
                        {s.subtitle}
                      </p>
                    )}
                    {s.title && (
                      <h2 className={`text-3xl md:text-5xl font-black mb-4 ${dark ? 'text-white' : 'text-slate-900'}`}>
                        {s.title}
                      </h2>
                    )}
                    {s.content && (
                      <p className={`max-w-2xl mx-auto text-lg ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
                        {s.content}
                      </p>
                    )}
                  </div>

                  {/* Showcase Cards Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                    {(s.meta?.items || []).map((item, idx) => {
                      const gradients = [
                        'from-orange-500 via-red-500 to-pink-500',
                        'from-blue-500 via-indigo-500 to-purple-500',
                        'from-teal-500 via-cyan-500 to-blue-500',
                        'from-purple-500 via-pink-500 to-rose-500',
                        'from-amber-500 via-orange-500 to-red-500',
                        'from-green-500 via-emerald-500 to-teal-500',
                      ];
                      const gradient = item.gradient || gradients[idx % gradients.length];
                      const CardTag = item.link ? Link : 'div';
                      const cardProps = item.link ? { to: item.link } : {};
                      return (
                        <CardTag
                          key={idx}
                          {...cardProps}
                          className="group relative rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 cursor-pointer"
                        >
                          {/* Image with gradient overlay */}
                          <div className="relative h-72 overflow-hidden">
                            {item.image ? (
                              <img
                                src={item.image}
                                alt={item.title || ''}
                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                            ) : (
                              <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
                            )}
                            {/* Gradient overlay */}
                            <div className={`absolute inset-0 bg-gradient-to-t ${gradient} opacity-80 group-hover:opacity-90 transition-opacity duration-500`} />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

                            {/* Badge */}
                            {item.badge && (
                              <div className="absolute top-4 left-4 z-10">
                                <span className="backdrop-blur-md bg-white/20 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border border-white/30 shadow-lg">
                                  {item.badge}
                                </span>
                              </div>
                            )}

                            {/* Content overlay */}
                            <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                              {item.icon && (
                                <div className="text-3xl mb-2">{item.icon}</div>
                              )}
                              <h3 className="text-xl font-black text-white mb-1 leading-tight">
                                {item.title}
                              </h3>
                              {item.desc && (
                                <p className="text-white/80 text-sm leading-relaxed line-clamp-2">
                                  {item.desc}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Bottom bar */}
                          <div className={`flex items-center justify-between px-6 py-4 ${dark ? 'bg-slate-800' : 'bg-white'}`}>
                            <span className={`text-sm font-bold ${dark ? 'text-slate-300' : 'text-slate-700'}`}>
                              {item.label || 'Explore'}
                            </span>
                            <div className={`h-9 w-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-md`}>
                              <ArrowRight size={16} className="text-white" />
                            </div>
                          </div>
                        </CardTag>
                      );
                    })}
                  </div>

                  {/* CTA Button */}
                  {s.button_link && (
                    <div className="text-center mt-12">
                      <Link
                        to={s.button_link}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-red-600 to-orange-500 text-white px-10 py-4 rounded-2xl font-black hover:shadow-2xl hover:shadow-red-500/30 transition-all duration-300 hover:scale-105"
                      >
                        {s.button_text || 'View All'}
                        <ArrowRight size={18} />
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        );
      })}

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-orange-50/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-4">
                <Star size={14} className="text-orange-500 fill-orange-500" />
                <span className="text-orange-600 font-bold text-xs uppercase tracking-wider">Bestsellers</span>
              </div>
              <h2 className="text-3xl md:text-5xl font-black text-slate-900 mb-4">Featured Products</h2>
              <p className="text-slate-600 text-lg">Discover our bestselling and newly launched products.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {featuredProducts.map((product) => {
                const image = product.featured_image || product.image || product.media?.[0]?.url || product.images?.[0];
                const promotion = product.offers?.find(o => o.is_active);
                
                return (
                  <div key={product.id} className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-2xl hover:shadow-red-500/10 transition-all duration-300 group hover:-translate-y-1">
                    <div className="h-56 bg-slate-100 flex items-center justify-center relative overflow-hidden">
                      {image ? (
                        <img src={image} alt={product.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      ) : (
                        <span className="text-slate-400 text-sm">Product Image</span>
                      )}
                      {promotion && (
                        <div className="absolute top-4 left-4 z-10">
                          <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                            <Tag size={12} />
                            {promotion.type === 'percentage' ? `${promotion.value}% OFF` : `₹${promotion.value} OFF`}
                          </span>
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </div>
                  <div className="p-5 space-y-3">
                    <h3 className="font-bold text-slate-900 group-hover:text-red-600 transition-colors">{product.name}</h3>
                    <p className="text-xs text-slate-500 line-clamp-2 leading-relaxed">{product.description}</p>
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div className="font-black text-lg text-slate-900">₹{product.discount_price || product.price}</div>
                      <Link to={`/shop/${product.slug}`} className="flex items-center gap-1 text-xs font-bold text-red-600 hover:text-red-700 transition group-hover:gap-2">View Details <ArrowRight size={12} /></Link>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
            <div className="text-center mt-12">
              <Link to="/shop" className="inline-flex items-center gap-2 bg-gradient-to-r from-slate-900 to-slate-800 text-white px-10 py-4 rounded-xl hover:shadow-2xl hover:shadow-slate-900/20 transition-all duration-300 font-black shadow-xl hover:scale-105">
                View All Products <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Local Spotlight */}
      <LocalSpotlight />

      {/* Middle Banner Ad Slot */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <AdSlot position="homepage_banner" className="h-[250px]" />
      </div>

      {/* Latest News & Insights - Enhanced */}
      {latestBlogs.length > 0 && (
        <section className="py-20 bg-gradient-to-br from-slate-50 via-white to-indigo-50/30">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between mb-12">
              <div>
                <div className="inline-flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-4 py-1.5 mb-3">
                  <TrendingUp size={14} className="text-red-500" />
                  <span className="text-red-600 font-bold text-xs uppercase tracking-wider">News & Blog</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Latest News & Insights</h2>
              </div>
              <Link to="/blog" className="hidden md:flex items-center gap-2 text-red-600 font-bold text-sm uppercase tracking-wider hover:gap-3 transition-all">
                View All <ArrowRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {latestBlogs.map((blog) => (
                <Link to={`/blog/${blog.slug}`} key={blog.id} className="group bg-white rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-slate-100 overflow-hidden flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    {blog.featured_image ? (
                      <img
                        src={blog.featured_image}
                        alt={blog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-500 to-teal-600 flex items-center justify-center">
                        <Sparkles size={40} className="text-white/30" />
                      </div>
                    )}
                    {blog.tags && blog.tags[0] && (
                      <div className="absolute top-4 left-4">
                        <span className="bg-red-600 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full shadow-lg">
                          {blog.tags[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-4 text-xs text-slate-400 mb-3">
                      <div className="flex items-center gap-1.5">
                        <Calendar size={14} className="text-red-500" />
                        {new Date(blog.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </div>
                      {blog.author && (
                        <div className="flex items-center gap-1.5">
                          <User size={14} className="text-red-500" />
                          {blog.author.name}
                        </div>
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-red-600 transition-colors line-clamp-2 leading-snug">
                      {blog.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-2">
                      {blog.excerpt || blog.content?.replace(/<[^>]*>/g, '').substring(0, 120) + '...'}
                    </p>
                    <div className="mt-auto flex items-center gap-1 text-red-600 text-xs font-black uppercase tracking-widest group-hover:gap-2 transition-all">
                      Read More <ChevronRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-12 md:hidden">
              <Link to="/blog" className="inline-flex items-center gap-2 text-red-600 font-bold text-sm uppercase tracking-wider">
                View All Articles <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Available At / Newsletter - Enhanced */}
      <section className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        {/* Decorative orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-4 py-1.5 mb-4">
                <MapPin size={14} className="text-red-400" />
                <span className="text-red-400 font-bold text-xs uppercase tracking-wider">Pan India</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-black mb-4">{sections.find(s => s.section_type === 'cta')?.title || 'Available Across India'}</h2>
              <p className="text-slate-300 mb-8 text-lg leading-relaxed">{sections.find(s => s.section_type === 'cta')?.content || 'Our products are available at leading retail stores, modern trade outlets, and digital commerce platforms across the country.'}</p>
              <Link to={sections.find(s => s.section_type === 'cta')?.button_link || '/dealers'} className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-3.5 rounded-xl hover:bg-red-600 hover:text-white transition-all duration-300 font-bold shadow-xl hover:scale-105">
                {sections.find(s => s.section_type === 'cta')?.button_text || 'Find a Dealer'} <ArrowRight size={16} />
              </Link>
            </div>
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg">
                  <Mail size={18} className="text-white" />
                </div>
                <h3 className="text-xl font-bold">Stay Updated</h3>
              </div>
              <p className="text-slate-400 text-sm mb-6">Subscribe to our newsletter for the latest products, offers, and industry insights.</p>
              {subscribed ? (
                <div className="bg-green-500/20 border border-green-500/30 text-green-300 p-4 rounded-xl flex items-center gap-2">
                  <Sparkles size={18} /> Thanks for subscribing to our newsletter!
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-3">
                  <input
                    type="email"
                    required
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-slate-800/50 border border-slate-600/50 text-white rounded-xl p-4 placeholder-slate-500 focus:outline-none focus:border-red-500 transition"
                  />
                  <button type="submit" className="w-full bg-gradient-to-r from-red-600 to-orange-500 text-white px-6 py-3.5 rounded-xl hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300 font-bold flex items-center justify-center gap-2">
                    Subscribe Now <ArrowRight size={16} />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;
